import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { compressImage } from "../lib/imageUtils";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { MessageSquare, Send, AlertCircle, ChevronLeft, Paperclip, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Messages() {
  const { conversationId } = useParams();
  const { t } = useI18n();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [pending, setPending] = useState(null); // {file, preview}
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const endRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastConversationIdRef = useRef(null);
  const lastMessageCountRef = useRef(0);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const r = await api.get("/messages/conversations");
        setConversations(r.data);
      } catch (_e) {/* */}
      setLoading(false);
    };
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [user]);

  useEffect(() => {
    if (!conversationId || !user) return;
    const load = async () => {
      try {
        const r = await api.get(`/messages/${conversationId}`);
        // Only auto-scroll to the bottom when:
        //   (a) the user just switched to this conversation (first load), OR
        //   (b) the user was already near the bottom AND a new message arrived.
        // This stops the polling tick (every 5s) from yanking the screen down
        // while the user is scrolling up to read earlier messages.
        const container = messagesContainerRef.current;
        const newConv = lastConversationIdRef.current !== conversationId;
        const gotNewMessage = r.data.length > lastMessageCountRef.current;
        let nearBottom = true;
        if (container && !newConv) {
          const dist =
            container.scrollHeight - container.scrollTop - container.clientHeight;
          nearBottom = dist < 120; // px from bottom — considered "still at the bottom"
        }
        setMessages(r.data);
        lastConversationIdRef.current = conversationId;
        lastMessageCountRef.current = r.data.length;

        if (newConv || (gotNewMessage && nearBottom)) {
          // Scroll only the INNER container, never the page itself. Using
          // scrollIntoView() also scrolls all ancestor scroll containers
          // (including window) which would push the whole page off-screen.
          setTimeout(() => {
            const c = messagesContainerRef.current;
            if (c) c.scrollTop = c.scrollHeight;
          }, 50);
        }
      } catch (_e) {/* */}
    };
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [conversationId, user]);

  if (!user) {
    return (
      <div className="max-w-md mx-auto py-20 px-6 text-center">
        <AlertCircle className="w-12 h-12 mx-auto text-[#C85A32]"/>
        <p className="mt-4 font-heading font-bold text-2xl">{t("login_required")}</p>
        <button onClick={login} className="mt-6 btn-primary" data-testid="msg-login-button">{t("cta_login")}</button>
      </div>
    );
  }

  const activeConv = conversations.find((c) => c.conversation_id === conversationId);

  const handlePickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Tik nuotraukos");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Per didelis failas (max 5MB)");
      return;
    }
    setPending({ file, preview: URL.createObjectURL(file) });
    e.target.value = "";
  };

  const clearPending = () => {
    if (pending?.preview) URL.revokeObjectURL(pending.preview);
    setPending(null);
  };

  const send = async () => {
    if (!activeConv) return;
    if (!text.trim() && !pending) return;
    let imagePath = null;
    if (pending) {
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", pending.file);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        imagePath = r.data.path;
      } catch (err) {
        toast.error(err.response?.data?.detail || "Nuotrauka nepavyko");
        setUploading(false);
        return;
      }
      setUploading(false);
    }
    try {
      await api.post("/messages", {
        listing_id: activeConv.listing_id,
        recipient_id: activeConv.other_user_id,
        text,
        image_path: imagePath,
      });
      setText("");
      clearPending();
      const r = await api.get(`/messages/${conversationId}`);
      setMessages(r.data);
      // Keep ref in sync so the next polling tick doesn't treat this as a "new" message arriving.
      lastMessageCountRef.current = r.data.length;
      setTimeout(() => {
        const c = messagesContainerRef.current;
        if (c) c.scrollTop = c.scrollHeight;
      }, 50);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Klaida");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="text-3xl font-heading font-black text-[#1A1A18] mb-6">{t("nav_messages")}</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[70vh]">
        {/* Conversations list */}
        <div className={`border border-[#D1CFC7] rounded-md overflow-hidden bg-white ${conversationId ? "hidden md:block" : ""}`}>
          <div className="overflow-y-auto h-full">
            {loading ? (
              <div className="p-4 text-sm text-[#7A7A73]">...</div>
            ) : conversations.length === 0 ? (
              <div className="p-4 text-sm text-[#7A7A73]">{t("no_messages")}</div>
            ) : (
              conversations.map((c) => (
                <Link
                  key={c.conversation_id}
                  to={`/messages/${c.conversation_id}`}
                  data-testid={`conv-${c.conversation_id}`}
                  className={`flex items-start gap-3 p-3 border-b border-[#D1CFC7] hover:bg-[#F0EFEB] transition-colors relative ${conversationId === c.conversation_id ? "bg-[#F0EFEB]" : ""}`}
                >
                  <div className="w-10 h-10 bg-[#F0EFEB] rounded-md flex-shrink-0 overflow-hidden">
                    {c.listing_image && <img src={fileUrl(c.listing_image)} alt="" className="w-full h-full object-cover"/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-bold text-sm text-[#1A1A18] truncate">{c.other_user_name}</p>
                    <p className="text-xs text-[#7A7A73] truncate">{c.listing_title}</p>
                    <p className="text-xs text-[#4A4A45] truncate mt-0.5">{c.last_text || "📷 Nuotrauka"}</p>
                  </div>
                  {c.unread > 0 && (
                    <span className="absolute top-3 right-3 bg-[#C85A32] text-white text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center">
                      {c.unread}
                    </span>
                  )}
                </Link>
              ))
            )}
          </div>
        </div>

        {/* Conversation */}
        <div className={`md:col-span-2 border border-[#D1CFC7] rounded-md flex flex-col bg-white overflow-hidden ${!conversationId ? "hidden md:flex" : "flex"}`}>
          {!conversationId ? (
            <div className="flex-1 flex items-center justify-center text-[#7A7A73]">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50"/>
                <p>Pasirink pokalbį</p>
              </div>
            </div>
          ) : (
            <>
              {activeConv && (
                <div className="border-b border-[#D1CFC7] p-3 flex items-center gap-3">
                  <button onClick={() => navigate("/messages")} className="md:hidden p-1.5 rounded hover:bg-[#F0EFEB]" data-testid="conv-back">
                    <ChevronLeft className="w-5 h-5"/>
                  </button>
                  <div className="w-10 h-10 bg-[#F0EFEB] rounded-md overflow-hidden">
                    {activeConv.listing_image && <img src={fileUrl(activeConv.listing_image)} alt="" className="w-full h-full object-cover"/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/listing/${activeConv.listing_id}`} className="text-sm font-bold text-[#1A1A18] hover:underline truncate block">
                      {activeConv.listing_title}
                    </Link>
                    <p className="text-xs text-[#7A7A73]">{activeConv.other_user_name}</p>
                  </div>
                </div>
              )}
              <div
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#FAF9F6]"
              >
                {messages.map((m) => {
                  const mine = m.sender_id === user.user_id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-md text-sm overflow-hidden ${mine ? "bg-[#1A1A18] text-white" : "bg-white border border-[#D1CFC7] text-[#1A1A18]"}`} data-testid={`msg-${m.id}`}>
                        {m.image_path && (
                          <button
                            type="button"
                            onClick={() => setLightbox(m.image_path)}
                            data-testid={`msg-img-${m.id}`}
                            className="block w-full"
                          >
                            <img
                              src={fileUrl(m.image_path)}
                              alt=""
                              className="w-full max-h-72 object-cover hover:opacity-90"
                            />
                          </button>
                        )}
                        {m.text && <div className="px-3 py-2 whitespace-pre-wrap break-words">{m.text}</div>}
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef}/>
              </div>

              {/* Pending image preview */}
              {pending && (
                <div className="border-t border-[#D1CFC7] p-3 bg-[#F0EFEB] flex items-center gap-3" data-testid="msg-pending-image">
                  <img src={pending.preview} alt="" className="w-16 h-16 rounded-md object-cover border border-[#D1CFC7]"/>
                  <div className="flex-1 text-sm text-[#4A4A45]">
                    <p className="font-semibold">
                      {uploading ? `Siunčiama... ${uploadProgress}%` : "Nuotrauka paruošta"}
                    </p>
                    <p className="text-xs">
                      {uploading
                        ? `${(pending.file.size / 1024).toFixed(0)} KB`
                        : "Pridėk tekstą (neprivaloma) ir spausk siųsti"}
                    </p>
                    {uploading && (
                      <div className="mt-1.5 h-1 bg-[#D1CFC7] rounded-full overflow-hidden">
                        <div className="h-full bg-[#C85A32] transition-all" style={{ width: `${uploadProgress}%` }}/>
                      </div>
                    )}
                  </div>
                  <button onClick={clearPending} disabled={uploading} className="p-1.5 hover:bg-white rounded disabled:opacity-30" data-testid="msg-clear-image">
                    <X className="w-4 h-4"/>
                  </button>
                </div>
              )}

              <div className="border-t border-[#D1CFC7] p-3 flex gap-2 items-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePickFile}
                  className="hidden"
                  data-testid="msg-file-input"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2 hover:bg-[#F0EFEB] rounded text-[#4A4A45] hover:text-[#C85A32]"
                  title="Pridėti nuotrauką"
                  data-testid="msg-attach-button"
                  disabled={uploading}
                >
                  <Paperclip className="w-5 h-5"/>
                </button>
                <input
                  data-testid="msg-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                  placeholder={t("type_message")}
                  className="flex-1 bg-white border border-[#D1CFC7] rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-[#C85A32]"
                />
                <button
                  onClick={send}
                  disabled={uploading || (!text.trim() && !pending)}
                  className="btn-primary !py-2 !px-3 disabled:opacity-50"
                  data-testid="msg-send"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Lightbox for image preview */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/90 z-[110] flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="msg-lightbox"
        >
          <button
            className="absolute top-4 right-4 p-2 bg-white/10 rounded-full text-white hover:bg-white/20"
            onClick={() => setLightbox(null)}
            data-testid="msg-lightbox-close"
          >
            <X className="w-6 h-6"/>
          </button>
          <img
            src={fileUrl(lightbox)}
            alt=""
            className="max-w-full max-h-[90vh] object-contain rounded-md"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
